<template>
  <div
    v-if="locationBasedInventoryEnabled"
    class="text-center"
    v-feature="PlatformFeature.INVENTORY"
  >
    <v-menu v-model="menu" :close-on-content-click="false" bottom offset-y>
      <template v-slot:activator="{ props }">
        <v-chip :color="'primary'" v-bind="props" label>
          {{ $t("common.action.location") }}
          <span v-if="chipValue">&nbsp;|&nbsp;</span>
          <span v-if="chipValue" class="font-weight-bold">{{ chipValue }}</span>
          <v-btn
            v-if="hasSpecificLocation"
            variant="text"
            size="x-small"
            icon="mdi-close"
            @click.stop="reset()"
          />
        </v-chip>
      </template>

      <v-card min-width="350" class="mt-2">
        <v-list>
          <v-list-item :title="$t('common.action.location')">
            <template v-slot:append>
              <v-btn variant="text" icon="mdi-close" @click="menu = false" />
            </template>
          </v-list-item>
        </v-list>

        <v-divider />

        <v-card-text>
          <v-select
            v-model="selectedLocationId"
            :items="locationOptions"
            item-title="title"
            item-value="value"
            :label="$t('common.action.location')"
            density="compact"
            variant="outlined"
            clearable
            hide-details
          >
            <template v-slot:selection="{ item }">
              <div class="d-flex align-center">
                <v-icon size="small" class="mr-2">
                  {{ getLocationItem(item).icon ?? "mdi-home-outline" }}
                </v-icon>
                <span>{{ getLocationItem(item).title }}</span>
              </div>
            </template>

            <template v-slot:item="{ item, props }">
              <v-list-item v-bind="props" :title="getLocationItem(item).title">
                <template v-slot:prepend>
                  <v-icon size="small">
                    {{ getLocationItem(item).icon ?? "mdi-home-outline" }}
                  </v-icon>
                </template>
              </v-list-item>
            </template>
          </v-select>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn @click="applyAndCloseFilter" color="primary" class="mr-2">
            {{ $t("page.payment.filter.applyFilter") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-menu>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from "vue";
import { storeToRefs } from "pinia";
import { useAccountStore } from "@/account/stores/account";
import { useAuthStore } from "@/auth/stores/auth";
import { useLocationStore } from "@/terminals/stores/locations";
import { useProductsStore } from "@/catalog/stores/products";
import { PlatformFeature } from "@/shared/enums/feature.enum";

type LocationOption = { title: string; value: string | null; icon: string };

const getLocationItem = (item: LocationOption | { raw?: LocationOption }) =>
  ("raw" in item && item.raw ? item.raw : item) as LocationOption;

const menu = ref(false);
const selectedLocationId = ref<string | null>(null);

const productsStore = useProductsStore();
const locationStore = useLocationStore();
const accountStore = useAccountStore();
const authStore = useAuthStore();

const { selectedLocation } = storeToRefs(locationStore);
const { user } = storeToRefs(authStore);

const locationBasedInventoryEnabled = computed(() => {
  const account = accountStore.account;
  return account?.accountSettings?.inventory?.location_based_inventory_enabled === true;
});

const accountId = computed(() => accountStore.account?.id ?? null);
const locations = computed(() => locationStore.globalLocations?.locations ?? []);
const assignedLocationIds = computed(() => {
  const id = accountId.value;
  if (!id) {
    return null;
  }
  return user.value?.accounts?.[id]?.assignedLocationIds ?? null;
});

const visibleLocations = computed(() => {
  const allLocations = locations.value;
  const ids = assignedLocationIds.value;

  if (!Array.isArray(ids)) {
    return allLocations;
  }

  const allowedLocationIds = new Set(ids);
  return allLocations.filter((location) => allowedLocationIds.has(location.id));
});

const appBarLocationId = computed(() => selectedLocation.value?.id ?? null);

const hasLocationFilter = computed(() => "locationId" in productsStore.filterProductCondition);

const filterLocationId = computed<unknown>(() => {
  if (!hasLocationFilter.value) return undefined;
  return productsStore.filterProductCondition.locationId;
});

const isGlobal = computed(() => hasLocationFilter.value && filterLocationId.value === null);

const explicitLocationId = computed(() => {
  const value = filterLocationId.value;
  return typeof value === "string" && value ? value : null;
});

const effectiveLocationId = computed(() => {
  if (isGlobal.value) return null;
  if (explicitLocationId.value) return explicitLocationId.value;
  return appBarLocationId.value;
});

const chipValue = computed(() => {
  if (effectiveLocationId.value === null) {
    return "Global";
  }

  const id = effectiveLocationId.value;
  if (!id) return "Global";
  const match = visibleLocations.value.find((location) => location.id === id);
  return match?.display_name ?? "";
});

const locationOptions = computed<LocationOption[]>(() => {
  const options: LocationOption[] = [
    {
      title: "Global",
      value: null,
      icon: "mdi-earth",
    },
    ...visibleLocations.value.map((location) => ({
      title: location.display_name,
      value: location.id,
      icon: "mdi-home-outline",
    })),
  ];

  const seen = new Set<string>();
  return options.filter((option) => {
    const key = option.value ?? "__GLOBAL__";
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
});

const hasSpecificLocation = computed(() => Boolean(explicitLocationId.value));

watch(
  () => menu.value,
  (isOpen) => {
    if (!isOpen) return;
    if (isGlobal.value) {
      selectedLocationId.value = null;
      return;
    }

    selectedLocationId.value = explicitLocationId.value ?? appBarLocationId.value ?? null;
  },
);

watch(
  () => [appBarLocationId.value, locationBasedInventoryEnabled.value] as const,
  async ([newLocationId, isEnabled], previousValue) => {
    const [previousLocationId, wasEnabled] = previousValue ?? [null, false];
    if (!isEnabled) {
      return;
    }
    if (wasEnabled && newLocationId === previousLocationId) {
      return;
    }
    if (hasLocationFilter.value) {
      const filterProductCondition = { ...productsStore.filterProductCondition };
      delete filterProductCondition.locationId;
      productsStore.setFilterProductCondition(filterProductCondition);
    }
    await productsStore.searchProducts({}, false);
  },
  { immediate: true },
);

watch(
  () => visibleLocations.value,
  async () => {
    if (!locationBasedInventoryEnabled.value) return;
    const override = explicitLocationId.value;
    if (!override) return;
    const stillVisible = visibleLocations.value.some((location) => location.id === override);
    if (stillVisible) return;

    const filterProductCondition = productsStore.filterProductCondition;
    filterProductCondition.locationId = null;
    productsStore.setFilterProductCondition(filterProductCondition);
    await productsStore.searchProducts({}, false);
  },
  { deep: true },
);

const reset = async () => {
  const filterProductCondition = productsStore.filterProductCondition;
  filterProductCondition.locationId = null;
  productsStore.setFilterProductCondition(filterProductCondition);
  await productsStore.searchProducts({}, false);
};

const applyAndCloseFilter = async () => {
  const nextId = selectedLocationId.value;
  const filterProductCondition = productsStore.filterProductCondition;

  if (!nextId) {
    filterProductCondition.locationId = null;
  } else {
    filterProductCondition.locationId = nextId;
  }

  productsStore.setFilterProductCondition(filterProductCondition);
  await productsStore.searchProducts({}, false);
  menu.value = false;
};
</script>
